import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, PhoneIcon, MailIcon, CalendarIcon, ClockIcon, DownloadIcon, FileSpreadsheetIcon, UserIcon, LockIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, FileTextIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Doctor, DoctorSchedule, Gender } from '../../types';
import { formatTime, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { exportDoctorsToExcel } from '../../utils/excelExport';
import { exportDoctorsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_NAMES: Record<string, string> = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo'
};
export function Doctors() {
  const {
    doctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    specialties,
    getSpecialty,
    getAppointmentsByDoctor,
    getPatient,
    getService
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('doctors');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-doctors-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  // Load deactivated/archived statuses for specialties (to filter them from dropdowns)
  const [specialtyStatuses, setSpecialtyStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-specialties-status');
      if (saved) setSpecialtyStatuses(JSON.parse(saved));
    } catch {}
    // Listen for storage changes from other tabs/modules
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-specialties-status' && e.newValue) {
        try {
          setSpecialtyStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isSpecialtyActive = (id: string) => !specialtyStatuses[id];
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateDoctor(id, {
      status: 'archived'
    } as any).catch(e => console.error('Error archiving doctor in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateDoctor(id, {
      status: 'deactivated'
    } as any).catch(e => console.error('Error deactivating doctor in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateDoctor(id, {
      status: 'active'
    } as any).catch(e => console.error('Error restoring doctor in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    gender: 'masculino' as Gender,
    specialtyId: '',
    phone: '',
    email: ''
  });
  const [scheduleData, setScheduleData] = useState<DoctorSchedule>({
    monday: {
      start: '08:00',
      end: '17:00'
    },
    tuesday: {
      start: '08:00',
      end: '17:00'
    },
    wednesday: {
      start: '08:00',
      end: '17:00'
    },
    thursday: {
      start: '08:00',
      end: '17:00'
    },
    friday: {
      start: '08:00',
      end: '14:00'
    },
    saturday: null,
    sunday: null
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredDoctors = doctors.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase()) || d.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = !filterSpecialty || d.specialtyId === filterSpecialty;
    const status = getItemStatus(d.id);
    return matchesSearch && matchesSpecialty && status === viewMode;
  });
  const {
    sortedItems: sortedDoctors,
    requestSort,
    isSorted
  } = useSortableData(filteredDoctors);
  const {
    paginatedItems: paginatedDoctors,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(sortedDoctors);
  // Counts for tabs
  const activeCount = doctors.filter(d => getItemStatus(d.id) === 'active').length;
  const archivedCount = doctors.filter(d => getItemStatus(d.id) === 'archived').length;
  const deactivatedCount = doctors.filter(d => getItemStatus(d.id) === 'deactivated').length;
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }, ...specialties.filter(s => isSpecialtyActive(s.id)).map(s => ({
    value: s.id,
    label: s.name
  }))];
  const specialtyOptionsForForm = specialties.filter(s => isSpecialtyActive(s.id)).map(s => ({
    value: s.id,
    label: s.name
  }));
  const openNewForm = () => {
    if (!canCreate('doctors')) return;
    setSelectedDoctor(null);
    setFormData({
      name: '',
      gender: 'masculino',
      specialtyId: '',
      phone: '',
      email: ''
    });
    setScheduleData({
      monday: {
        start: '08:00',
        end: '17:00'
      },
      tuesday: {
        start: '08:00',
        end: '17:00'
      },
      wednesday: {
        start: '08:00',
        end: '17:00'
      },
      thursday: {
        start: '08:00',
        end: '17:00'
      },
      friday: {
        start: '08:00',
        end: '14:00'
      },
      saturday: null,
      sunday: null
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (doctor: Doctor) => {
    if (!canEdit('doctors')) return;
    setSelectedDoctor(doctor);
    setFormData({
      name: doctor.name,
      gender: doctor.gender || 'masculino',
      specialtyId: doctor.specialtyId,
      phone: doctor.phone,
      email: doctor.email
    });
    setScheduleData(doctor.schedule);
    setFormErrors({});
    setIsViewOpen(false);
    setIsFormOpen(true);
  };
  const openView = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.specialtyId) errors.specialtyId = 'La especialidad es requerida';
    if (!formData.phone) errors.phone = 'El teléfono es requerido';
    if (!formData.email) errors.email = 'El correo es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedDoctor) {
      updateDoctor(selectedDoctor.id, {
        ...formData,
        schedule: scheduleData
      });
    } else {
      addDoctor({
        ...formData,
        schedule: scheduleData
      });
    }
    setIsFormOpen(false);
  };
  const openActionConfirm = (doctor: Doctor, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedDoctor(doctor);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedDoctor || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedDoctor.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedDoctor.id);
    } else if (actionType === 'delete') {
      deleteDoctor(selectedDoctor.id);
      setIsActionConfirmOpen(false);
      setIsViewOpen(false);
    }
  };
  const toggleDaySchedule = (day: keyof DoctorSchedule) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? null : {
        start: '08:00',
        end: '17:00'
      }
    }));
  };
  const updateDaySchedule = (day: keyof DoctorSchedule, field: 'start' | 'end', value: string) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? {
        ...prev[day]!,
        [field]: value
      } : null
    }));
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <ClockIcon className="w-4 h-4" data-id="element-2074" />
  }, {
    id: 'schedule',
    label: 'Horario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-2075" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-2076" />
  }];
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = doctors;
    if (dateFrom && dateTo) {
      dataToExport = doctors.filter(d => {
        const dateStr = new Date(d.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportDoctorsToExcel(dataToExport, (specialtyId: string) => {
      const specialty = getSpecialty(specialtyId);
      return specialty?.name || '-';
    });
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = doctors;
    if (dateFrom && dateTo) {
      dataToExport = doctors.filter(d => {
        const dateStr = new Date(d.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportDoctorsToPdf(dataToExport, (specialtyId: string) => {
      const specialty = getSpecialty(specialtyId);
      return specialty?.name || '-';
    });
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-2077">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-600 via-sky-500 to-cyan-500 p-6 text-white shadow-xl" data-id="element-2078">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2079" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-2080">
          <div data-id="element-2081">
            <div className="flex items-center gap-3 mb-2" data-id="element-2082">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-2083">
                <UserIcon className="w-6 h-6" data-id="element-2084" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-2085">Doctores</h1>
            </div>
            <p className="text-sky-100 max-w-xl" data-id="element-2086">
              Gestión de doctores y especialidades
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-2087">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-2088">
            {canExportPdf && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-2090" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2089">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2092" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2091">
                Exportar Excel
              </Button>}
            {canCreate('doctors') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2094" />} onClick={openNewForm} className="bg-white text-sky-700 hover:bg-sky-50 shadow-lg shadow-sky-900/20" data-id="element-2093">
                Nuevo Doctor
              </Button>}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-2095">
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto" data-id="element-2096">
          <div className="w-full sm:w-64 relative" data-id="element-2097">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2098" />
            <Input placeholder="Buscar por nombre o correo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-2099" />
          </div>
          <div className="w-full sm:w-64" data-id="element-2100">
            <Select options={specialtyOptions} value={filterSpecialty} onChange={setFilterSpecialty} placeholder="Filtrar por especialidad" data-id="element-2101" />
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-2102">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2103">
              <tab.icon className="w-4 h-4" data-id="element-2104" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-2105">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-2106">
          <LockIcon className="w-4 h-4" data-id="element-2107" />
          <span data-id="element-2108">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            doctores.
          </span>
        </div>}

      {/* Doctors List */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-2109">
        <div className="h-1 bg-gradient-to-r from-sky-500 to-cyan-500" data-id="element-2110" />
        <div className="overflow-x-auto" data-id="element-2111">
          <table className="w-full" data-id="element-2112">
            <thead data-id="element-2113">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2114">
                <SortableHeader label="Doctor" sortKey="name" currentDirection={isSorted('name')} onSort={requestSort} data-id="element-2115" />
                <SortableHeader label="Especialidad" sortKey="specialtyId" currentDirection={isSorted('specialtyId')} onSort={requestSort} data-id="element-2116" />
                <SortableHeader label="Contacto" sortKey="phone" currentDirection={isSorted('phone')} onSort={requestSort} data-id="element-2117" />
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2118">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2119">
              {filteredDoctors.length === 0 ? <tr data-id="element-2120">
                  <td colSpan={4} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-2121">
                    <div className="flex flex-col items-center justify-center" data-id="element-2122">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-2123">
                        <UserIcon className="w-8 h-8 text-gray-400" data-id="element-2124" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-2125">
                        No se encontraron doctores
                      </h3>
                      <p className="text-sm" data-id="element-2126">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando un nuevo doctor' : 'No hay doctores en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedDoctors.map(doctor => {
              const specialty = getSpecialty(doctor.specialtyId);
              return <tr key={doctor.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-2127">
                      <td className="py-4 px-6" data-id="element-2128">
                        <div className="flex items-center gap-3" data-id="element-2129">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-sm" data-id="element-2130">
                            {doctor.name.charAt(0).toUpperCase()}
                          </div>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-2131">
                            {getDoctorPrefix(doctor.gender, specialty?.name)}{' '}
                            {doctor.name}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-2132">
                        <Badge variant="primary" className="bg-sky-50 text-sky-700 border border-sky-100" data-id="element-2133">
                          {specialty?.name || '-'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6" data-id="element-2134">
                        <div className="space-y-1" data-id="element-2135">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1.5" data-id="element-2136">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2137" />{' '}
                            {doctor.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5" data-id="element-2138">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2139" />{' '}
                            {doctor.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-2140">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2141">
                          <button onClick={() => openView(doctor)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-2142">
                            <EyeIcon className="w-4 h-4" data-id="element-2143" />
                          </button>

                          {viewMode === 'active' ? <>
                              {canEdit('doctors') && <button onClick={() => openEditForm(doctor)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-2144">
                                  <EditIcon className="w-4 h-4" data-id="element-2145" />
                                </button>}
                              {canDelete('doctors') && <>
                                  <button onClick={() => openActionConfirm(doctor, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-2146">
                                    <ArchiveIcon className="w-4 h-4" data-id="element-2147" />
                                  </button>
                                  <button onClick={() => openActionConfirm(doctor, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-2148">
                                    <PowerOffIcon className="w-4 h-4" data-id="element-2149" />
                                  </button>
                                </>}
                            </> : <button onClick={() => restoreItem(doctor.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-2150">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-2151" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-2152">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-2153" />
        </div>
      </Card>

      {/* Doctor Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedDoctor ? 'Editar Doctor' : 'Nuevo Doctor'} size="lg" icon={<UserIcon className="w-5 h-5" data-id="element-2155" />} accentColor="sky" data-id="element-2154">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2156">
          {/* Personal Info Section */}
          <div data-id="element-2157">
            <div className="flex items-center gap-2 mb-4" data-id="element-2158">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-2159"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2160">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2161">
              <div className="grid grid-cols-2 gap-4" data-id="element-2162">
                <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} error={formErrors.name} placeholder="Nombre del doctor" data-id="element-2163" />
                <Select label="Género" options={[{
                value: 'masculino',
                label: 'Masculino'
              }, {
                value: 'femenino',
                label: 'Femenino'
              }]} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value as Gender
              })} data-id="element-2164" />
              </div>
              <Select label="Especialidad" options={specialtyOptionsForForm} value={formData.specialtyId} onChange={value => setFormData({
              ...formData,
              specialtyId: value
            })} error={formErrors.specialtyId} data-id="element-2165" />
            </div>
          </div>

          {/* Contact Section */}
          <div data-id="element-2166">
            <div className="flex items-center gap-2 mb-4" data-id="element-2167">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-2168"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2169">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2170">
              <div className="grid grid-cols-2 gap-4" data-id="element-2171">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} error={formErrors.phone} placeholder="809-000-0000" data-id="element-2172" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="doctor@email.com" data-id="element-2173" />
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          <div data-id="element-2174">
            <div className="flex items-center gap-2 mb-4" data-id="element-2175">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-2176"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2177">
                Horario de Atención
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2178">
              <div className="space-y-1" data-id="element-2179">
                {DAYS.map(day => {
                const isActive = scheduleData[day] !== null;
                return <div key={day} className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${isActive ? 'bg-white dark:bg-gray-800 border border-sky-100 dark:border-sky-800/30 shadow-sm' : 'border border-transparent'}`} data-id="element-2180">
                      <label className="flex items-center gap-3 w-36 cursor-pointer select-none" data-id="element-2181">
                        <div className="relative" data-id="element-2182">
                          <input type="checkbox" checked={isActive} onChange={() => toggleDaySchedule(day)} className="sr-only peer" data-id="element-2183" />
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isActive ? 'bg-sky-500 border-sky-500 dark:bg-sky-600 dark:border-sky-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}`} data-id="element-2184">
                            {isActive && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-2185">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-2186" />
                              </svg>}
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-2187">
                          {DAY_NAMES[day]}
                        </span>
                      </label>
                      {isActive && <div className="flex items-center gap-2 flex-1" data-id="element-2188">
                          <Input type="time" value={scheduleData[day]!.start} onChange={e => updateDaySchedule(day, 'start', e.target.value)} className="w-32" data-id="element-2189" />
                          <span className="text-gray-400 text-sm font-medium" data-id="element-2190">
                            a
                          </span>
                          <Input type="time" value={scheduleData[day]!.end} onChange={e => updateDaySchedule(day, 'end', e.target.value)} className="w-32" data-id="element-2191" />
                        </div>}
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2192">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-2193">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-2194">
              {selectedDoctor ? 'Guardar Cambios' : 'Crear Doctor'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Doctor View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Detalles del Doctor" size="lg" data-id="element-2195">
        {selectedDoctor && <div className="space-y-6" data-id="element-2196">
            {/* Hero Header */}
            <div className="relative rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-2197">
              <div className="px-6 py-5" data-id="element-2198">
                <div className="flex items-center gap-4" data-id="element-2199">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/50 dark:to-blue-900/50 flex items-center justify-center text-3xl font-bold text-sky-600 dark:text-sky-400 shadow-sm border border-gray-100 dark:border-gray-700 flex-shrink-0" data-id="element-2200">
                    {selectedDoctor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2201">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white truncate" data-id="element-2202">
                      {getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name)}{' '}
                      {selectedDoctor.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-2203">
                      {getSpecialty(selectedDoctor.specialtyId)?.name}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0" data-id="element-2204">
                    {!readOnly && viewMode === 'active' && <>
                        {canEdit('doctors') && <Button variant="outline" size="sm" onClick={() => openEditForm(selectedDoctor)} data-id="element-2205">
                            <EditIcon className="w-4 h-4 mr-2" data-id="element-2206" /> Editar
                          </Button>}
                        {canDelete('doctors') && <Button variant="danger" size="sm" onClick={() => openActionConfirm(selectedDoctor, 'archive')} data-id="element-2207">
                            <ArchiveIcon className="w-4 h-4" data-id="element-2208" />
                          </Button>}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-2209">
              <TabPanel id="info" activeTab={activeTab} data-id="element-2210">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2211">
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-2212">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2213">
                      <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-600 dark:text-sky-400" data-id="element-2214">
                        <PhoneIcon className="w-4 h-4" data-id="element-2215" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-2216">
                        Teléfono
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-2217">
                      {selectedDoctor.phone}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-2218">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2219">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-600 dark:text-cyan-400" data-id="element-2220">
                        <MailIcon className="w-4 h-4" data-id="element-2221" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-2222">
                        Correo Electrónico
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-2223">
                      {selectedDoctor.email}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-2224">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2225">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-2226">
                        <UserIcon className="w-4 h-4" data-id="element-2227" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-2228">
                        Género
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11 capitalize" data-id="element-2229">
                      {selectedDoctor.gender || 'No especificado'}
                    </p>
                  </div>
                </div>
              </TabPanel>

              <TabPanel id="schedule" activeTab={activeTab} data-id="element-2230">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2231">
                  {DAYS.map(day => <div key={day} className={`flex items-center justify-between p-3 rounded-lg border ${selectedDoctor.schedule[day] ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-800/50 border-transparent'}`} data-id="element-2232">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-2233">
                        {DAY_NAMES[day]}
                      </span>
                      {selectedDoctor.schedule[day] ? <span className="text-sm font-medium text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20 px-2 py-1 rounded" data-id="element-2234">
                          {formatTime(selectedDoctor.schedule[day]!.start)} -{' '}
                          {formatTime(selectedDoctor.schedule[day]!.end)}
                        </span> : <span className="text-sm text-gray-400 italic" data-id="element-2235">
                          No disponible
                        </span>}
                    </div>)}
                </div>
              </TabPanel>

              <TabPanel id="appointments" activeTab={activeTab} data-id="element-2236">
                <div className="space-y-4" data-id="element-2237">
                  {getAppointmentsByDoctor(selectedDoctor.id).length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-2238">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2239" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-2240">
                        No hay citas registradas
                      </p>
                    </div> : getAppointmentsByDoctor(selectedDoctor.id).map(apt => {
                const patient = getPatient(apt.patientId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-2241">
                          <div className="flex items-center gap-4" data-id="element-2242">
                            <div className="w-10 h-10 rounded-full bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center text-sky-600 dark:text-sky-400 font-bold" data-id="element-2243">
                              {patient?.name.charAt(0)}
                            </div>
                            <div data-id="element-2244">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-2245">
                                {patient?.name}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-2246">
                                <span data-id="element-2247">{apt.date}</span>
                                <span data-id="element-2248">•</span>
                                <span data-id="element-2249">{formatTime(apt.time)}</span>
                                <span data-id="element-2250">•</span>
                                <span data-id="element-2251">{service?.name}</span>
                              </div>
                            </div>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-2252">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                </div>
              </TabPanel>
            </Tabs>
          </div>}
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Doctor' : actionType === 'deactivate' ? 'Desactivar Doctor' : 'Eliminar Doctor'} size="sm" data-id="element-2253">
        <div className="space-y-4" data-id="element-2254">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-2255">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedDoctor?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedDoctor?.name}"? No aparecerá en las listas activas.`}
            {actionType === 'delete' && `¿Estás seguro de que deseas eliminar permanentemente a "${selectedDoctor?.name}"? Esta acción no se puede deshacer.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-2256">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-2257">
              Cancelar
            </Button>
            <Button variant={actionType === 'delete' ? 'danger' : 'primary'} onClick={handleAction} data-id="element-2258">
              {actionType === 'archive' ? 'Archivar' : actionType === 'deactivate' ? 'Desactivar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Doctores" isExporting={isExporting} data-id="element-2259" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Doctores a PDF" isExporting={isPdfExporting} data-id="element-2260" />
    </div>;
}